import React from 'react';
import { HOUR_SECONDS, GAP_COLOR, fmt } from './defaults';

// Pure SVG doughnut chart. Each row-segment renders as a distinct slice
// at its actual chronological position (00:00 anchored at 12 o'clock,
// then clockwise). Empty time up to 60:00 shows as a neutral gap slice.
//
// showMarkers=true adds clock face indicators:
//   * a short tick for every minute (60 ticks)
//   * a longer tick + label for every quarter hour (0/15/30/45)
export default function DoughnutChart({ segments, size = 280, showMarkers = false, centerLabel = 'Σύνολο' }) {
  const cx = size / 2;
  const cy = size / 2;
  // Leave room for outer minute markers + labels when enabled.
  const outerPadding = showMarkers ? size * 0.13 : size * 0.07;
  const rO = size / 2 - outerPadding;
  const rI = rO * 0.52;

  const used = segments.reduce((a, s) => a + s.dur, 0);
  const denom = Math.max(used, HOUR_SECONDS);
  const gap = used < HOUR_SECONDS ? HOUR_SECONDS - used : 0;

  let ang = 0;
  const paths = [];
  const push = (dur, color, key) => {
    const a0 = ang;
    const a1 = ang + (2 * Math.PI * dur) / denom;
    ang = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + rO * Math.sin(a0);
    const y0 = cy - rO * Math.cos(a0);
    const x1 = cx + rO * Math.sin(a1);
    const y1 = cy - rO * Math.cos(a1);
    const xi1 = cx + rI * Math.sin(a1);
    const yi1 = cy - rI * Math.cos(a1);
    const xi0 = cx + rI * Math.sin(a0);
    const yi0 = cy - rI * Math.cos(a0);
    paths.push(
      <path
        key={key}
        d={`M${x0} ${y0} A${rO} ${rO} 0 ${large} 1 ${x1} ${y1} L${xi1} ${yi1} A${rI} ${rI} 0 ${large} 0 ${xi0} ${yi0} Z`}
        fill={color}
        stroke="#fff"
        strokeWidth="1"
      />
    );
  };
  segments.forEach((s, i) => push(s.dur, s.color, `seg-${i}`));
  if (gap > 0) push(gap, GAP_COLOR, 'gap');

  // Clock markers (minute ticks + quarter labels)
  const markers = [];
  if (showMarkers) {
    for (let m = 0; m < 60; m++) {
      const isQuarter = m % 15 === 0;
      const a = (2 * Math.PI * m) / 60;
      const inner = rO + 2;
      const outer = rO + (isQuarter ? 10 : 5);
      const x1 = cx + inner * Math.sin(a);
      const y1 = cy - inner * Math.cos(a);
      const x2 = cx + outer * Math.sin(a);
      const y2 = cy - outer * Math.cos(a);
      markers.push(
        <line
          key={`tick-${m}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={isQuarter ? '#1B2A4A' : '#94A3B8'}
          strokeWidth={isQuarter ? 2.4 : 1}
          strokeLinecap="round"
        />
      );
      if (isQuarter) {
        const lblR = rO + 20;
        const lx = cx + lblR * Math.sin(a);
        const ly = cy - lblR * Math.cos(a) + 4;
        markers.push(
          <text
            key={`lbl-${m}`}
            x={lx}
            y={ly}
            textAnchor="middle"
            fontSize={Math.max(10, size * 0.035)}
            fontWeight="700"
            fill="#1B2A4A"
          >
            {m === 0 ? '00' : String(m).padStart(2, '0')}
          </text>
        );
      }
    }
  }

  const centerFontSize = Math.max(18, size * 0.09);
  const centerLabelSize = Math.max(11, size * 0.05);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {markers}
      {paths}
      <text x={cx} y={cy - centerLabelSize / 2 - 2} textAnchor="middle" fontSize={centerLabelSize} fill="#555">
        {centerLabel}
      </text>
      <text
        x={cx}
        y={cy + centerFontSize / 2}
        textAnchor="middle"
        fontSize={centerFontSize}
        fontWeight="700"
        fill="#1B2A4A"
      >
        {fmt(used)}
      </text>
    </svg>
  );
}
